import React, { useMemo, useRef } from 'react'
import { Box, Flex } from '@chakra-ui/core'
import { BackButton } from 'components/TasksV2/UI/BackButton'
import { BaseModalV2 } from 'components/ui/BaseModal'
import {
  InsightsContentStack,
  InsightsContentType,
  InsightsStackProvider,
  useInsightsContext,
} from '../InsightsStackProvider'
import MediaResultsPreview from '../Content/AllContextMediaResultsPreview'
import { MediaGalleryReview } from '../Content/MediaGalleryReview'
import useLocaleText from 'components/useLocaleText'
import styles from './styles.module.css'

interface IProps {
  onClose: () => void
  tid: string
  content?: InsightsContentStack
  selectedRecurrenceTs: string | null
}

const InsightsModalContent: React.FC<IProps> = ({ onClose, tid }) => {
  const { insights, contentStack, closeContent } = useInsightsContext()

  const { title } = insights[tid]

  const t_media_overview = useLocaleText('t_media_overview')
  const initialFocusRef = useRef<HTMLDivElement>(null)

  const currentContent = contentStack[contentStack.length - 1]

  const headerTitle = useMemo(() => {
    if (!currentContent) return null

    const { contentType, props } = currentContent

    switch (contentType) {
      case InsightsContentType.INSIGHTS:
        return title
      case InsightsContentType.ALL_CONTEXTS_MEDIA_RESULTS_PREVIEW:
        return t_media_overview
      case InsightsContentType.ALL_CONTEXTS_MEDIA_RESULTS_OVERVIEW:
        return props.title
      default:
        return null
    }
  }, [currentContent, t_media_overview, title])

  if (currentContent?.contentType === InsightsContentType.ALL_CONTEXTS_MEDIA_RESULTS_OVERVIEW) {
    return <MediaGalleryReview entry={currentContent.props.entry} onClose={closeContent} />
  }

  return (
    <>
      <BaseModalV2
        floating
        isOpen={true}
        onClose={onClose}
        modalProps={{ initialFocusRef }}
        modalContentProps={{
          ref: initialFocusRef,
          tabIndex: -1,
          minW: 0,
          className: styles.modal,
        }}
        modalHeaderProps={{ px: '4px', py: '2px' }}
        modalBodyProps={{
          height: '100%',
          maxH: 'none',
          minH: 0,
          flex: 1,
          px: 0,
          py: 0,
          bg: 'white',
          overflow: 'hidden',
        }}
        modalFooterProps={{ display: 'none' }}
        header={
          <Flex justifyContent="space-between" minH="48px" alignItems="center">
            <BackButton title={headerTitle} event={closeContent} />
          </Flex>
        }
        body={
          <>
            {contentStack.map((content, index) => {
              const { contentType } = content
              const isTopContent = index === contentStack.length - 1

              switch (contentType) {
                case InsightsContentType.ALL_CONTEXTS_MEDIA_RESULTS_PREVIEW:
                  return (
                    <Box
                      display={isTopContent ? 'block' : 'none'}
                      h="100%"
                      minH={0}
                      key={`${index}-${content}`}
                      overflow="hidden"
                    >
                      <MediaResultsPreview key={index} />
                    </Box>
                  )
                default:
                  return null
              }
            })}
          </>
        }
        footer={<></>}
      />
    </>
  )
}
export const InsightsModal: React.FC<IProps> = (props) => {
  return (
    <InsightsStackProvider
      content={props.content}
      tid={props.tid}
      onClose={props.onClose}
      recurrenceTs={props.selectedRecurrenceTs}
    >
      <InsightsModalContent {...props} />
    </InsightsStackProvider>
  )
}
